/**
 * White label theme transformation utilities
 * Transforms siteBrandings GQL response into
 * - ui-uds color overrides
 * - white label theme merged into the emotion theme context as theme.whiteLabelTheme
 */

import {
  SiteBrandingResponse,
  EntityBranding,
  WhiteLabel,
  WhiteLabelCustomTheme,
  WhiteLabelThemeOverride,
} from '../types/white-label-theme';

/**
 * Default values for missing theme properties.
 */
const WHITE_LABEL_THEME_DEFAULTS = {
  baseColors: { link: '#3957E1', cta: '#3957E1', hover: '#2F48BB' },
  colors: {
    headerBackground: '#F9F9F9',
    heroBackground: '#FFFFFF',
    heroText: '#222222',
    progressBar: '#3957E1',
    cta: '#3957E1',
  },
  isLogoTransparent: false,
} as const;

/**
 * Finds the last entityBranding in the array that has a whiteLabel property.
 * Returns null if none found.
 *
 * @param entityBrandings - Array of entity brandings from siteBranding response
 * @returns The last EntityBranding with whiteLabel, or null
 */
export function findWhiteLabelBranding(
  entityBrandings?: (EntityBranding | null)[] | null
): EntityBranding | null {
  if (!entityBrandings?.length) {
    return null;
  }

  // Iterate backwards to find the last one with whiteLabel
  for (let i = entityBrandings.length - 1; i >= 0; i--) {
    const branding = entityBrandings[i];
    if (branding?.whiteLabel) {
      return branding;
    }
  }

  return null;
}

/**
 * Transforms whiteLabel properties into the custom theme object for emotion context.
 * Used by getWhiteLabelThemeOverrides to populate theme.whiteLabelTheme.
 *
 * @param whiteLabel - WhiteLabel data from entityBranding (can be partial/null/undefined)
 * @returns WhiteLabelCustomTheme object with all fields filled
 */
export function getWhiteLabelCustomTheme(
  whiteLabel?: WhiteLabel | null
): WhiteLabelCustomTheme {
  if (!whiteLabel) {
    return getDefaultWhiteLabelCustomTheme();
  }
  // todo remove defaults
  const ctaColor = whiteLabel.ctaColor ?? WHITE_LABEL_THEME_DEFAULTS.colors.cta;

  return {
    isLogoTransparent:
      whiteLabel.isLogoTransparent ??
      WHITE_LABEL_THEME_DEFAULTS.isLogoTransparent,
    baseColors: {
      link: ctaColor,
      cta: ctaColor,
      hover: WHITE_LABEL_THEME_DEFAULTS.baseColors.hover,
    },
    colors: {
      headerBackground:
        whiteLabel.headerBackgroundColor ??
        WHITE_LABEL_THEME_DEFAULTS.colors.headerBackground,
      heroBackground:
        whiteLabel.heroBackgroundColor ??
        WHITE_LABEL_THEME_DEFAULTS.colors.heroBackground,
      heroText:
        whiteLabel.heroTextColor ?? WHITE_LABEL_THEME_DEFAULTS.colors.heroText,
      progressBar:
        whiteLabel.progressBarColor ??
        WHITE_LABEL_THEME_DEFAULTS.colors.progressBar,
      cta: ctaColor,
    },
  };
}

/**
 * Default custom theme when siteBrandings response is missing or has no whiteLabel.
 * Uses neutral Updater branding as fallback.
 *
 * @returns Default WhiteLabelCustomTheme with Updater branding
 */
export function getDefaultWhiteLabelCustomTheme(): WhiteLabelCustomTheme {
  return {
    isLogoTransparent: WHITE_LABEL_THEME_DEFAULTS.isLogoTransparent,
    baseColors: { ...WHITE_LABEL_THEME_DEFAULTS.baseColors },
    colors: { ...WHITE_LABEL_THEME_DEFAULTS.colors },
  };
}

/**
 * Returns combined theme overrides for the UDS ThemeProvider's customTheme prop.
 * Finds the last entityBranding with whiteLabel and transforms it.
 *
 * Merges via lodash in ThemeProvider, resulting in:
 *   - theme.colors.* — UDS cta/link/hover color overrides
 *   - theme.whiteLabelTheme — full custom theme accessible in styled components
 *
 * Always returns a value — falls back to Updater defaults when response is absent,
 * ensuring theme.whiteLabelTheme is always populated in the emotion context.
 *
 * @param siteBranding - siteBranding response from GQL query (can be partial/null/undefined)
 * @returns Partial Theme object for ThemeProvider's customTheme prop
 *
 * @example
 * // In a provider component:
 * const { data } = useSiteBrandingQuery();
 * const themeOverrides = useMemo(
 *   () => getWhiteLabelThemeOverrides(data?.siteBranding),
 *   [data?.siteBranding]
 * );
 *
 * return (
 *   <ThemeProvider customTheme={themeOverrides}>
 *     {children}
 *   </ThemeProvider>
 * );
 */
export function getWhiteLabelThemeOverrides(
  siteBranding?: SiteBrandingResponse | null
): WhiteLabelThemeOverride {
  const branding = findWhiteLabelBranding(siteBranding?.entityBrandings);

  // Only include whiteLabelTheme if branding has whiteLabel data
  if (!branding?.whiteLabel) {
    return {};
  }

  const whiteLabelTheme = getWhiteLabelCustomTheme(branding.whiteLabel);

  return {
    colors: whiteLabelTheme.baseColors,
    whiteLabelTheme,
    ...(branding.whiteLabel.ctaColor && {
      button: { useInsetHover: true },
    }),
  };
}
